import { Controller } from "@hotwired/stimulus"

import { mapProvinceSum2MapData } from "../echart-helper"

export default class extends Controller {
  static targets = ["tableBody"]

  connect() {
    this.sortKey = "newArea"
    this.sortDirection = "desc"
    this.initializeAttempt = 0
    this.initializeChartsWhenVisible()
  }

  initializeChartsWhenVisible() {
    const comparisonElement = document.getElementById("contract-provice-area-comparison-chart")
    const mapElement = document.getElementById("contract-provice-area-china-chart")

    if ((!comparisonElement?.clientWidth || !mapElement?.clientWidth) && this.initializeAttempt < 20) {
      this.initializeAttempt += 1
      this.initializeTimer = setTimeout(() => this.initializeChartsWhenVisible(), 50)
      return
    }

    this.initializeComparisonChart()
    this.initializeMapChart()
    this.resizeTimer = setTimeout(() => this.layout(), 200)
  }

  initializeComparisonChart() {
    const element = document.getElementById("contract-provice-area-comparison-chart")
    if (!element) return

    const provinceRows = Array.from(this.tableBodyTarget.querySelectorAll("tr[data-province]"))
      .map((row) => ({
        province: row.dataset.province,
        currentRate: this.numberValue(row.dataset.sortCurrentRate),
        previousRate: this.numberValue(row.dataset.sortPreviousRate)
      }))
      .sort((a, b) => (b.currentRate ?? -1) - (a.currentRate ?? -1))

    const currentLabel = element.dataset.currentLabel
    const previousLabel = element.dataset.previousLabel
    const changeLabel = element.dataset.changeLabel
    const unit = element.dataset.unit
    const axisColor = this.cssColor("--cui-body-color", "#4b5563")
    const mutedColor = this.cssColor("--cui-secondary-color", "#7b8492")
    const borderColor = this.cssColor("--cui-border-color", "#d8dbe0")
    const maxRate = Math.max(...provinceRows.flatMap((row) => [row.currentRate || 0, row.previousRate || 0]))

    this.comparisonChart = echarts.init(element)
    this.comparisonChart.setOption({
      animationDuration: 500,
      color: ["#4f8ef7", "#a7b0c0"],
      grid: {
        top: 54,
        right: 46,
        bottom: 42,
        left: 126,
        containLabel: false
      },
      legend: {
        top: 8,
        right: 16,
        textStyle: { color: axisColor },
        itemWidth: 12,
        itemHeight: 8
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        renderMode: "richText",
        formatter: (params) => {
          const row = provinceRows[params[0].dataIndex]
          const current = row.currentRate == null ? "-" : `${row.currentRate}${unit}`
          const previous = row.previousRate == null ? "-" : `${row.previousRate}${unit}`
          const change = row.currentRate == null || row.previousRate == null
            ? "-"
            : `${row.currentRate - row.previousRate >= 0 ? "+" : ""}${(row.currentRate - row.previousRate).toFixed(2)}${unit}`

          return `${row.province}\n${currentLabel}  ${current}\n${previousLabel}  ${previous}\n${changeLabel}  ${change}`
        }
      },
      xAxis: {
        type: "value",
        min: 0,
        max: Math.max(10, Math.ceil(maxRate / 5) * 5),
        name: unit,
        nameTextStyle: { color: mutedColor },
        axisLabel: {
          color: mutedColor,
          formatter: `{value}${unit}`
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: borderColor, type: "dashed", opacity: 0.6 } }
      },
      yAxis: {
        type: "category",
        inverse: true,
        data: provinceRows.map((row) => row.province),
        axisLabel: {
          color: axisColor,
          fontSize: 12,
          margin: 12
        },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series: [
        {
          name: currentLabel,
          type: "bar",
          barMaxWidth: 9,
          data: provinceRows.map((row) => row.currentRate),
          itemStyle: { borderRadius: [0, 4, 4, 0] },
          label: {
            show: true,
            position: "right",
            color: axisColor,
            fontSize: 10,
            formatter: (params) => params.value == null ? "" : params.value
          }
        },
        {
          name: previousLabel,
          type: "bar",
          barMaxWidth: 9,
          data: provinceRows.map((row) => row.previousRate),
          itemStyle: { borderRadius: [0, 4, 4, 0] }
        }
      ]
    })
  }

  initializeMapChart() {
    const element = document.getElementById("contract-provice-area-china-chart")
    if (!element) return

    this.mapChart = echarts.init(element)

    const provinceSum = JSON.parse(this.data.get("province_sum"))
    const yearRateSum = JSON.parse(this.data.get("year_rate_sum"))
    const previousYearRateSum = JSON.parse(this.data.get("previous_year_rate_sum"))
    const mapData = provinceSum.map(mapProvinceSum2MapData)
    const chinaMap = echarts.getMap("china")

    const renderMap = (mapFeatures) => {
      const geoCoordinates = {}
      mapFeatures.forEach((feature) => {
        geoCoordinates[feature.properties.name] = feature.properties.cp
      })

      const scatterData = mapData.flatMap((item, index) => {
        const coordinates = geoCoordinates[item.name]
        if (!coordinates) return []

        return [{
          name: item.name,
          value: coordinates.concat([yearRateSum[index], previousYearRateSum[index]])
        }]
      })

      this.mapChart.setOption({
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            if (params.seriesType === "scatter") {
              return `${params.name}<br />本期占有率：${params.value[2]}%<br />上年同期：${params.value[3]}%`
            }

            return `${params.name}<br />新开工面积：${Number(params.value || 0).toLocaleString()} 万㎡`
          }
        },
        visualMap: {
          show: true,
          type: "continuous",
          min: Math.min(...provinceSum) * 0.05,
          max: Math.max(...provinceSum),
          left: "center",
          bottom: 14,
          orient: "horizontal",
          seriesIndex: [1],
          calculable: true,
          text: ["高", "低"],
          textStyle: { color: this.cssColor("--cui-body-color", "#4b5563") },
          inRange: { color: ["#dff0fa", "#2394cf", "#092838"] }
        },
        toolbox: {
          show: true,
          right: 12,
          top: 8,
          feature: { restore: {}, saveAsImage: {} }
        },
        geo: {
          map: "china",
          roam: true,
          scaleLimit: { min: 1, max: 5 },
          layoutCenter: ["50%", "45%"],
          layoutSize: "92%",
          label: { show: false },
          emphasis: {
            label: { show: false },
            itemStyle: { areaColor: "#4499d0" }
          },
          itemStyle: { areaColor: "#dff0fa", borderColor: "#7fb8d7" }
        },
        series: [
          {
            name: "占有率",
            type: "scatter",
            coordinateSystem: "geo",
            data: scatterData,
            symbolSize: 9,
            z: 3,
            label: {
              formatter: "{b}",
              position: "right",
              color: this.cssColor("--cui-body-color", "#2d3e53"),
              fontSize: 10,
              show: true
            },
            itemStyle: {
              color: (params) => {
                const rate = params.value[2]
                if (rate >= 10) return "#7f2704"
                if (rate >= 5) return "#d94801"
                if (rate >= 3) return "#f16913"
                if (rate >= 1) return "#fdae6b"
                return "#ffffff"
              },
              borderColor: "#7f2704",
              borderWidth: 1
            }
          },
          {
            name: "新开工面积",
            type: "map",
            map: "china",
            geoIndex: 0,
            data: mapData,
            label: { show: false },
            emphasis: {
              label: { show: true },
              itemStyle: { areaColor: "#62a9d1" }
            },
            itemStyle: { borderColor: "#7fb8d7" }
          }
        ]
      }, true)
    }

    if (chinaMap) {
      renderMap(chinaMap.geoJson.features)
    } else {
      this.mapRequest = $.get("/china.geojson", (chinaJson) => {
        if (!this.element.isConnected) return
        echarts.registerMap("china", chinaJson)
        renderMap(echarts.getMap("china").geoJson.features)
      })
    }
  }

  sort(event) {
    const button = event.currentTarget
    const key = button.dataset.sortKey
    const type = button.dataset.sortType
    const direction = key === this.sortKey
      ? (this.sortDirection === "asc" ? "desc" : "asc")
      : (type === "number" ? "desc" : "asc")
    const rows = Array.from(this.tableBodyTarget.querySelectorAll("tr"))

    rows.sort((leftRow, rightRow) => {
      const leftValue = leftRow.dataset[`sort${this.capitalize(key)}`]
      const rightValue = rightRow.dataset[`sort${this.capitalize(key)}`]
      const leftMissing = leftValue == null || leftValue === ""
      const rightMissing = rightValue == null || rightValue === ""

      if (leftMissing && rightMissing) return 0
      if (leftMissing) return 1
      if (rightMissing) return -1

      const comparison = type === "number"
        ? Number(leftValue) - Number(rightValue)
        : leftValue.localeCompare(rightValue, "zh-Hans-CN", { numeric: true })

      return direction === "asc" ? comparison : -comparison
    })

    rows.forEach((row) => this.tableBodyTarget.appendChild(row))
    this.sortKey = key
    this.sortDirection = direction
    this.updateSortHeaders(button, direction)
  }

  updateSortHeaders(activeButton, direction) {
    this.element.querySelectorAll(".market-share-sort").forEach((button) => {
      const active = button === activeButton
      const header = button.closest("th")
      const indicator = button.querySelector(".market-share-sort-indicator")

      header.setAttribute("aria-sort", active ? (direction === "asc" ? "ascending" : "descending") : "none")
      indicator.textContent = active ? (direction === "asc" ? "↑" : "↓") : "↕"
      button.classList.toggle("is-active", active)
    })
  }

  layout() {
    this.resizeChartIfVisible(this.comparisonChart)
    this.resizeChartIfVisible(this.mapChart)
  }

  disconnect() {
    clearTimeout(this.initializeTimer)
    clearTimeout(this.resizeTimer)
    this.mapRequest?.abort()
    this.comparisonChart?.dispose()
    this.mapChart?.dispose()
  }

  numberValue(value) {
    return value == null || value === "" ? null : Number(value)
  }

  capitalize(value) {
    return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
  }

  cssColor(variable, fallback) {
    return getComputedStyle(this.element).getPropertyValue(variable).trim() || fallback
  }

  resizeChartIfVisible(chart) {
    const element = chart?.getDom()
    if (element?.clientWidth && element?.clientHeight) chart.resize()
  }
}
